import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import useReactHookFormStyles from '../../../../custom-components/form-components/ReactHookForm.styles';

import { fetchOptions } from '../queries';

const Select = ({ control, name, fieldName, componentName, filterRef, query, setHasOptions, uniqueValues = true, props: { label, labelKey, options: configOptions, variant, required, shouldUnregister, ...rest } }) => {
    const {
        clearErrors,
        formState: { errors },
        getValues,
        setError,
        updateSelectedValues,
        selectedValues,
        watch,
    } = useFormContext();
    const classes = useReactHookFormStyles();
    const [options, setOptions] = useState(configOptions);
    const [filteredOptions, setFilteredOptions] = useState(configOptions);

    const { endpoint, where = {}, fields, order, roleConstraints, populate, useLoopback } = query || {};
    const { referenceField, filterKey } = filterRef || {};

    const filterAndSetOptions = (optionList) => {
        // Fetch all corresponding selects for this field array
        const existingSelects = selectedValues[fieldName]?.map((field) => field[componentName]);
        const currentId = getValues(name);
        // If the option is not selected selected or is currently selected, keep the option
        // Filter the options that have already been selected
        const filtered = optionList?.filter((option) => !existingSelects?.includes(option.id) || option.id === currentId);
        if (filtered) setFilteredOptions(filtered);
    };

    useEffect(
        () => {
            const value = getValues(referenceField);

            const getOptions = async () => {
                const whereClause = { ...where };
                // extend where object for filtering options
                whereClause[filterKey] = value;
                const optionsResponse = await fetchOptions({ endpoint, where: whereClause, fields, order, roleConstraints, populate, useLoopback });

                if (optionsResponse.error) {
                    setHasOptions(false);
                    setOptions([]);
                    setFilteredOptions([]);
                    setError(name, { type: 'fetch options', message: `Could not fetch ${label} options` });
                } else {
                    if (optionsResponse.length) setHasOptions(true);
                    // set original options list into state
                    setOptions(optionsResponse);
                    // filter and set options
                    filterAndSetOptions(optionsResponse);
                    clearErrors([name]);
                }
            };

            if (!uniqueValues) return;
            if (value) {
                getOptions();
            } else {
                setHasOptions(false);
                setOptions([]);
                setFilteredOptions([]);
            }
        },
        !!referenceField ? [watch(referenceField)] : []
    );

    // Relying on selectedValues to trigger filtering
    // useFormContext().watch() does not trigger when these nested field array values change
    useEffect(() => {
        if (uniqueValues) {
            // filter and set options when values change
            filterAndSetOptions(options);
        }
    }, [selectedValues]);

    const getOptionLabel = (option) => {
        if (!option) {
            return '';
        }
        const key = labelKey || 'name';
        if (option[key]) {
            return option[key];
        }
        const foundOption = filteredOptions?.find((opt) => (opt.id || opt._id) === option);
        return foundOption?.[key] || '';
    };

    const renderInput = (props) => (
        <TextField
            {...props}
            label={label}
            required={required}
            variant={variant || 'outlined'}
            error={errors[name]}
            helperText={errors[name]?.message}
            // wrap
        />
    );

    if (!filteredOptions && !errors[name]) {
        return (
            <TextField
                value="Loading options"
                disabled
                required={required}
                label={label}
                variant={variant || 'outlined'}
                className={classes.textField}
                // wrap
            />
        );
    }

    return (
        <FormControl className={classes.formController}>
            <Controller
                name={name}
                control={control}
                shouldUnregister={shouldUnregister}
                render={({ field: { onChange, value } }) => {
                    return (
                        <Autocomplete
                            value={value || null}
                            onChange={(e, item) => {
                                onChange(item?.id || item?._id || item || null);
                                const fieldArrayValue = getValues(fieldName);
                                updateSelectedValues(fieldName, fieldArrayValue);
                            }}
                            options={filteredOptions}
                            renderInput={renderInput}
                            getOptionLabel={getOptionLabel}
                            getOptionSelected={(option, val) => {
                                const valId = val?.id || val?._id || val;
                                const optionId = option?.id || option?._id || option;
                                return valId === optionId;
                            }}
                            {...rest}
                            // wrap
                        />
                    );
                }}
            />
        </FormControl>
    );
};

export default Select;
